import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Texer - the flying mosaic. ONE big cover cut into a 20 x 20 grid of thin
// tiles; every tile carries its own FRAGMENT of the artwork. Electro,
// house, electronic pop, synthwave. After Winamp AVS's Superscope + Texer
// and the demoscene of the 2000s.
//
// Two states and the passage between them:
//   SWARM  the tiles fly along the closed curves of the library (spiro,
//          torus knot, helix...), two or three nested curves at once, and
//          TUMBLE about a random axis of their own, one turn a bar - one
//          sees fragments, backs and specular flashes in turn.
//   FORM   every tile takes its (i, j) place on a 3D surface, front out:
//          the whole cover reads, big. Flag, cylinder, sphere, cube, wave.
//   ASSEMBLY (two beats, centre first) and EXPLOSION (one beat, edges
//   first): a Bezier arc whose control point is pushed radially, with a
//   delay per tile by its distance to the grid's centre.
// position = bezier(curve(s, t), form(i, j, t), easing(elapsed - delay(i, j)))
// ALL of it in the vertex shader; the CPU writes uniforms, never a position.
//
// The cycle: 8 bars = 6 bars of swarm + 2 bars of form, the form landing on
// the downbeat of bar 7; a new form every cycle. A drop assembles the flag
// at once, holds it four bars with a strong wave, then explodes.
//
// Trails: Echo's feedback loop, decay 0.90 in the swarm and 0.80 in the
// form. The tiles are drawn TWICE: once into the feedback buffer as flat
// quads of their MEAN colour (a static attribute computed when the cover
// loads) - those are the trails, ribbons in the cover's own palette - and
// once in the shared scene, lit and detailed, on top of the trails.

// Shared-rig contract: forbidden. Tiles on black and their trails; a wash
// would put a floor under them.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'texer',
  family: 'particles',
  danceability: 0.78,
  energyLevel: 0.74,
  bpmMin: 100,
  bpmMax: 170,
  movement: 0.75,
  tonality: 0.65,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// --- the curves (the swarm) ----------------------------------------------------
const CURVE_SPIRO = 0, CURVE_TORUS = 1, CURVE_HELIX = 2, CURVE_LISSAJOUS = 3;
const CURVE_ROSE = 4, CURVE_LEMNISCATE = 5, CURVE_SPHERE = 6;
const CURVE_NAMES = {
  spiro: CURVE_SPIRO, torusknot: CURVE_TORUS, helix: CURVE_HELIX, lissajous: CURVE_LISSAJOUS,
  rose: CURVE_ROSE, lemniscate: CURVE_LEMNISCATE, sphere: CURVE_SPHERE
};
// Integer ratios only: a figure closes on the bar, the tiles are back at
// their starting point on the downbeat.
const PRESETS = [
  { curve: CURVE_SPIRO, p: [3, 7, 0.45, 0.30] },
  { curve: CURVE_SPIRO, p: [5, 8, 0.40, 0.25] },
  { curve: CURVE_SPIRO, p: [2, 5, 0.50, 0.35] },
  { curve: CURVE_TORUS, p: [2, 3, 0.30, 0] },
  { curve: CURVE_TORUS, p: [3, 5, 0.28, 0] },
  { curve: CURVE_TORUS, p: [2, 7, 0.25, 0] },
  { curve: CURVE_HELIX, p: [3, 0, 0.55, 2.2] },
  { curve: CURVE_LISSAJOUS, p: [3, 2, 1, Math.PI / 2] },
  { curve: CURVE_LISSAJOUS, p: [5, 4, 3, 0] },
  { curve: CURVE_LISSAJOUS, p: [2, 3, 4, Math.PI / 4] },
  { curve: CURVE_ROSE, p: [3, 2, 0.35, 0] },
  { curve: CURVE_ROSE, p: [5, 3, 0.30, 0] },
  { curve: CURVE_LEMNISCATE, p: [0.5, 0, 0, 0] },
  { curve: CURVE_SPHERE, p: [0.15, 0, 0, 0] }
];
const NESTED_SCALES = [1.0, 0.72, 0.50];   // the two or three nested curves

// --- the forms -------------------------------------------------------------------
const FORM_FLAG = 0, FORM_CYLINDER = 1, FORM_SPHERE = 2, FORM_CUBE = 3, FORM_WAVE = 4;
const FORM_NAMES = { flag: FORM_FLAG, cylinder: FORM_CYLINDER, sphere: FORM_SPHERE, cube: FORM_CUBE, wave: FORM_WAVE };
// copies: how many 400-tile sets the form needs (cube: six faces; the
// cylinder repeats the cover three times round). tile: the tile's scale in
// the form relative to the flag's 1/20.
const FORMS = [
  { id: FORM_FLAG, copies: 1, tile: 1.0 },
  { id: FORM_CYLINDER, copies: 3, tile: 1.0 },
  { id: FORM_SPHERE, copies: 1, tile: 1.45 },
  { id: FORM_CUBE, copies: 6, tile: 0.555 },
  { id: FORM_WAVE, copies: 1, tile: 1.0 }
];

// --- the frame -------------------------------------------------------------------
const CAMERA_FOV = 50;
const CAMERA_Z = 6.5;
// Visible height at the figure's plane, world units; everything is a
// fraction of it.
const VIS_H = 2 * CAMERA_Z * Math.tan((CAMERA_FOV * Math.PI / 180) / 2);
const FORM_SIZE = 0.90 * VIS_H;          // the flag's side
const SWARM_RADIUS = 0.85 * VIS_H / 2;
const SWARM_TILE_FRAC = 0.03;            // of the screen height, in flight
const TILE_DEPTH = 0.04;                 // of the side
const FLAG_WAVE = 0.08;                  // of the side
const WAVE_RELIEF = 0.12;
const WAVE_TILT = -55 * Math.PI / 180;
const CYL_SPIN_BARS = 4;
const SPHERE_SPIN_BARS = 6;
const CUBE_SPIN_BARS = 6;
const LIGHT_BARS = 8;                    // one revolution of the key light

// --- the cycle, in beats ---------------------------------------------------------
const CYCLE_BARS = 8;
const ASSEMBLE_BEATS = 2;
const ASSEMBLE_DELAY = 0.5;              // 0..0.5 beats by the distance to the centre
const EXPLODE_BEATS = 1;
const EXPLODE_DELAY = 0.5;
const FORM_AT_BEAT = 24;                 // the downbeat of bar 7
const DROP_HOLD_BARS = 4;
const DROP_WAVE = 2.5;
const ASSEMBLE_BULGE = 0.35;             // of the swarm's radius
const EXPLODE_BULGE = 0.9;
const EXTRA_FADE_BEATS = 2;

// --- the beat ----------------------------------------------------------------------
const BEAT_PULSE = 0.2;
const PULSE_ATTACK_S = 0.06;
const PULSE_RELEASE_S = 0.20;
const SWARM_FLASH_FRAC = 0.10;
const FLASH_WAVE_SIGMA = 0.06;

// --- the trails ------------------------------------------------------------------
const DECAY_SWARM = 0.90;
const DECAY_FORM = 0.80;
const TRAIL_GAIN = 0.85;
const FLOOR_SUB = 1.5 / 255;
const CEILING = 0.92;

const SAFE_FLOOR_SWARM = 0.5;
const SAFE_FLOOR_FORM = 0.35;
const SHARED_SAFE_FLOOR = 0.32;
const FREE_BPM = 124;

const TIER_LAYOUT = {
  2: { grid: 20, copies: 6, trails: true, scale: 1.0 },
  1: { grid: 20, copies: 1, trails: true, scale: 0.75 },
  0: { grid: 15, copies: 1, trails: false, scale: 1.0 }
};
const DEFAULT_TIER = 2;

const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };
const _m4 = new THREE.Matrix4();
const _axis = new THREE.Vector3();

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}

// ---------------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------------

const SAFE_CHUNK = `
  uniform float uSafeFloor;
  // Multiplies, never adds: the attenuation is a gain on the final colour.
  float txSafeAtt(vec4 clipPos) {
    float a = safeZoneAttenuation(clipPos);
    return uSafeFloor + (a - ${SHARED_SAFE_FLOOR.toFixed(2)}) * (1.0 - uSafeFloor) / ${(1 - SHARED_SAFE_FLOOR).toFixed(2)};
  }
`;

// The curve library. th = 2 pi fract(s + u): folded BEFORE the harmonics,
// or sin(8 * 2pi * (s + 1)) differs from sin(8 * 2pi * s) by float rounding
// and the figure does not close on the bar.
const CURVE_CHUNK = `
  #define TAU 6.28318530718
  #define PI 3.14159265359

  vec3 txCurve(float id, float s, float u, vec4 P) {
    float th = TAU * fract(s + u);
    if (id < 0.5) {
      return vec3(cos(th * P.x) + P.z * cos(th * P.y),
                  sin(th * P.x) - P.z * sin(th * P.y),
                  P.w * sin(th * (P.y - P.x))) / (1.0 + P.z);
    } else if (id < 1.5) {
      float R = 0.62;
      float w = R + P.z * cos(th * P.y);
      return vec3(w * cos(th * P.x), w * sin(th * P.x), P.z * sin(th * P.y)) / (R + P.z);
    } else if (id < 2.5) {
      float strand = step(0.5, s);
      float t = fract(s * 2.0);
      float ang = TAU * P.x * t + strand * PI + TAU * fract(u);
      return vec3(P.z * cos(ang), (t - 0.5) * P.w, P.z * sin(ang)) / max(P.z, P.w * 0.5);
    } else if (id < 3.5) {
      return vec3(sin(th * P.x + P.w), sin(th * P.y), sin(th * P.z)) * 0.85;
    } else if (id < 4.5) {
      float r = cos(th * P.x);
      return vec3(r * cos(th), r * sin(th), P.z * sin(th * P.y));
    } else if (id < 5.5) {
      float sn = sin(th), cs = cos(th);
      float d = 1.0 + sn * sn;
      return vec3(cs / d, sn * cs / d, P.x * sin(2.0 * th)) * 0.95;
    }
    // SPHERE: a Fibonacci spiral breathing once a bar (s is the tile's own).
    float y = 1.0 - 2.0 * s;
    float r = sqrt(max(1.0 - y * y, 0.0));
    float phi = s * 400.0 * 2.39996323;
    return vec3(r * cos(phi), y, r * sin(phi)) * (0.85 + P.x * sin(TAU * fract(u)));
  }
`;

// Quaternions: the tumble is an axis-angle, the form's orientation a basis;
// the passage between them is a normalised lerp.
const QUAT_CHUNK = `
  vec4 qAxisAngle(vec3 axis, float a) { float h = a * 0.5; return vec4(axis * sin(h), cos(h)); }

  // r, u, f: the images of x, y, z (columns of the rotation matrix).
  vec4 qFromBasis(vec3 r, vec3 u, vec3 f) {
    float t = r.x + u.y + f.z;
    if (t > 0.0) {
      float s = sqrt(t + 1.0) * 2.0;
      return vec4((u.z - f.y) / s, (f.x - r.z) / s, (r.y - u.x) / s, 0.25 * s);
    } else if (r.x > u.y && r.x > f.z) {
      float s = sqrt(1.0 + r.x - u.y - f.z) * 2.0;
      return vec4(0.25 * s, (u.x + r.y) / s, (f.x + r.z) / s, (u.z - f.y) / s);
    } else if (u.y > f.z) {
      float s = sqrt(1.0 + u.y - r.x - f.z) * 2.0;
      return vec4((u.x + r.y) / s, 0.25 * s, (f.y + u.z) / s, (f.x - r.z) / s);
    }
    float s = sqrt(1.0 + f.z - r.x - u.y) * 2.0;
    return vec4((f.x + r.z) / s, (f.y + u.z) / s, 0.25 * s, (r.y - u.x) / s);
  }

  mat3 qToMat(vec4 q) {
    float x = q.x, y = q.y, z = q.z, w = q.w;
    return mat3(1.0 - 2.0 * (y * y + z * z), 2.0 * (x * y + z * w), 2.0 * (x * z - y * w),
                2.0 * (x * y - z * w), 1.0 - 2.0 * (x * x + z * z), 2.0 * (y * z + x * w),
                2.0 * (x * z + y * w), 2.0 * (y * z - x * w), 1.0 - 2.0 * (x * x + y * y));
  }

  vec4 qNlerp(vec4 a, vec4 b, float e) {
    if (dot(a, b) < 0.0) b = -b;
    return normalize(mix(a, b, e));
  }
`;

// The forms: the tile's centre and basis (normal out, up) for cell (cx, cy)
// in (-0.5, 0.5) and copy c. S is the flag's side.
const FORM_CHUNK = `
  uniform float uForm;
  uniform float uFormSize;     // S
  uniform float uCopies;       // the form's copies, as visible
  uniform float uBars;         // continuous bar count
  uniform float uWaveAmp;
  uniform vec3 uBands;
  uniform mat3 uFormRot;       // the cube's own rotation

  void txForm(vec2 c, float copy, out vec3 pos, out vec3 n, out vec3 up) {
    float S = uFormSize;
    if (uForm < 0.5) {
      // FLAG: a full plane, one sine wave travelling across it a bar.
      float A = ${FLAG_WAVE.toFixed(3)} * S * uWaveAmp;
      float ph = TAU * (c.x * 1.5 - uBars);
      pos = vec3(c.x * S, c.y * S, A * sin(ph));
      float slope = A * TAU * 1.5 * cos(ph) / S;
      n = normalize(vec3(-slope, 0.0, 1.0));
      up = vec3(0.0, 1.0, 0.0);
    } else if (uForm < 1.5) {
      // CYLINDER: the cover wrapped uCopies times round, a turn in four bars.
      float th = TAU * (copy + c.x + 0.5) / uCopies + TAU * uBars / ${CYL_SPIN_BARS.toFixed(1)};
      float R = S * uCopies / TAU;
      n = vec3(sin(th), 0.0, cos(th));
      pos = vec3(n.x * R, c.y * S, n.z * R);
      up = vec3(0.0, 1.0, 0.0);
    } else if (uForm < 2.5) {
      // SPHERE: equirectangular, turning.
      float lon = TAU * (c.x + 0.5) + TAU * uBars / ${SPHERE_SPIN_BARS.toFixed(1)};
      float lat = PI * c.y;
      float R = S * 0.25;
      float cl = cos(lat), sl = sin(lat);
      n = vec3(cl * sin(lon), sl, cl * cos(lon));
      pos = n * R;
      up = vec3(-sl * sin(lon), cl, -sl * cos(lon));
    } else if (uForm < 3.5) {
      // CUBE: copy = face. Side 0.555 S, turning about a tilted axis.
      float side = S * 0.555;
      vec3 fn, fu;
      if (copy < 0.5) { fn = vec3(0.0, 0.0, 1.0); fu = vec3(0.0, 1.0, 0.0); }
      else if (copy < 1.5) { fn = vec3(1.0, 0.0, 0.0); fu = vec3(0.0, 1.0, 0.0); }
      else if (copy < 2.5) { fn = vec3(0.0, 0.0, -1.0); fu = vec3(0.0, 1.0, 0.0); }
      else if (copy < 3.5) { fn = vec3(-1.0, 0.0, 0.0); fu = vec3(0.0, 1.0, 0.0); }
      else if (copy < 4.5) { fn = vec3(0.0, 1.0, 0.0); fu = vec3(0.0, 0.0, -1.0); }
      else { fn = vec3(0.0, -1.0, 0.0); fu = vec3(0.0, 0.0, 1.0); }
      vec3 fr = cross(fu, fn);
      pos = uFormRot * (fr * (c.x * side) + fu * (c.y * side) + fn * (side * 0.5));
      n = uFormRot * fn;
      up = uFormRot * fu;
    } else {
      // WAVE: a plane leaning back, a ridge of the bands' relief crossing
      // it each bar (lows left, mids centre, highs right).
      float band = c.x < -0.1667 ? uBands.x : (c.x < 0.1667 ? uBands.y : uBands.z);
      float A = ${WAVE_RELIEF.toFixed(3)} * S * (0.4 + 0.6 * band) * uWaveAmp;
      float ph = TAU * (c.y * 2.0 - uBars);
      float h = A * sin(ph);
      float slope = A * TAU * 2.0 * cos(ph) / S;
      float ct = cos(${WAVE_TILT.toFixed(5)}), st = sin(${WAVE_TILT.toFixed(5)});
      vec3 lp = vec3(c.x * S * 1.1, c.y * S * 1.1, h);
      vec3 ln = normalize(vec3(0.0, -slope, 1.0));
      vec3 lu = normalize(vec3(0.0, 1.0, slope));
      pos = vec3(lp.x, lp.y * ct - lp.z * st, lp.y * st + lp.z * ct);
      n = vec3(ln.x, ln.y * ct - ln.z * st, ln.y * st + ln.z * ct);
      up = vec3(lu.x, lu.y * ct - lu.z * st, lu.y * st + lu.z * ct);
    }
  }
`;

const tileVertexShader = `
  precision highp float;

  attribute vec2 aCell;      // (i, j), j from the top
  attribute float aCopy;
  attribute float aS;        // the tile's parameter along its curve, static
  attribute vec3 aAxis;      // the tumble axis
  attribute float aRand;
  attribute vec3 aMean;      // the tile's mean colour

  uniform float uGrid;
  uniform float uMode;       // 0 swarm, 1 assembling, 2 form, 3 exploding
  uniform float uElapsed;    // beats since the mode began
  uniform float uBulge;      // the Bezier control point's radial push, of the radius
  uniform float uExtra;      // visibility of the copies beyond the first
  uniform float uPhase;      // bar phase: the curve parameter every tile advances by
  uniform float uRadius;
  uniform float uNCurves;
  uniform vec4 uCurve0, uCurve1, uCurve2;        // (id, scale, -, -)
  uniform vec4 uParams0, uParams1, uParams2;
  uniform float uTileSwarm;
  uniform float uTileForm;
  uniform float uPulse;
  uniform float uBeatPhase;
  uniform float uBeatCount;
  uniform float uFlashFrac;

  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vView;
  varying vec3 vLocalN;
  varying vec3 vMean;
  varying vec2 vCell;
  varying float vFlash;
  varying float vSafe;

  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_CHUNK}
  ${CURVE_CHUNK}
  ${QUAT_CHUNK}
  ${FORM_CHUNK}

  void main() {
    float k = aCopy * uGrid * uGrid + aCell.y * uGrid + aCell.x;
    vec2 c = vec2((aCell.x + 0.5) / uGrid - 0.5, 0.5 - (aCell.y + 0.5) / uGrid);
    float distNorm = length(c) / 0.7071;

    // --- the swarm: one of the nested curves, closing on the bar ---
    float which = mod(k, uNCurves);
    vec4 cv = which < 0.5 ? uCurve0 : (which < 1.5 ? uCurve1 : uCurve2);
    vec4 cp = which < 0.5 ? uParams0 : (which < 1.5 ? uParams1 : uParams2);
    vec3 pSwarm = txCurve(cv.x, aS, uPhase, cp) * (uRadius * cv.y);

    // --- the form ---
    vec3 pForm, fn, fu;
    txForm(c, aCopy, pForm, fn, fu);

    // --- the passage: per-tile eased progress, centre first on assembly,
    // edges first on explosion ---
    float e;
    if (uMode < 0.5) e = 0.0;
    else if (uMode < 1.5) e = clamp((uElapsed - ${ASSEMBLE_DELAY.toFixed(2)} * distNorm) / ${ASSEMBLE_BEATS.toFixed(2)}, 0.0, 1.0);
    else if (uMode < 2.5) e = 1.0;
    else e = 1.0 - clamp((uElapsed - ${EXPLODE_DELAY.toFixed(2)} * (1.0 - distNorm)) / ${EXPLODE_BEATS.toFixed(2)}, 0.0, 1.0);
    e = e * e * (3.0 - 2.0 * e);

    // The arc: a quadratic Bezier whose control point is the midpoint
    // pushed radially.
    vec3 mid = (pSwarm + pForm) * 0.5;
    vec3 ctrl = mid + normalize(mid + vec3(0.001, 0.002, 0.0)) * (uBulge * uRadius);
    float ie = 1.0 - e;
    vec3 p = ie * ie * pSwarm + 2.0 * ie * e * ctrl + e * e * pForm;

    // The orientation: the tumble (a turn a bar about the tile's own axis),
    // finishing into the form's basis.
    vec4 qt = qAxisAngle(aAxis, TAU * (uBars + aRand));
    vec4 qf = qFromBasis(cross(fu, fn), fu, fn);
    mat3 R = qToMat(qNlerp(qt, qf, e));

    // The size: 3% of the height in flight, the form's 1/20 assembled; the
    // beat's pulse in flight; the extra copies fade with uExtra.
    float vis = aCopy < 0.5 ? 1.0 : uExtra;
    float size = mix(uTileSwarm * (1.0 + ${BEAT_PULSE.toFixed(2)} * uPulse), uTileForm, e) * vis;
    vec3 local = position * vec3(size, size, size * ${TILE_DEPTH.toFixed(3)});
    vec3 world = p + R * local;

    vUv = uv;
    vLocalN = normal;
    vNormal = R * normal;
    vView = cameraPosition - world;
    vMean = aMean;
    vCell = aCell;
    // Flashes: in flight a tenth of the tiles on the beat; in the form a
    // wave of white crossing the cover diagonally over the beat.
    float h = fract(sin(dot(vec2(k, uBeatCount), vec2(12.9898, 78.233))) * 43758.5453);
    float swarmFlash = (h < uFlashFrac ? 1.0 : 0.0) * uPulse;
    float d = (c.x + c.y + 1.0) * 0.5 - uBeatPhase;
    float formFlash = exp(-d * d / ${(2 * FLASH_WAVE_SIGMA * FLASH_WAVE_SIGMA).toFixed(5)});
    vFlash = mix(swarmFlash, formFlash, e);

    gl_Position = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
    vSafe = txSafeAtt(gl_Position);
  }
`;

// The lit tile. Front: its fragment of the cover under a turning key light
// with a tight white specular (the flash, over 0.9 for the bloom). Back: the
// palette's colour, dark. Edge: white.
const tileFragmentShader = `
  precision mediump float;

  uniform sampler2D uCover;
  uniform float uHasCover;
  // highp in the vertex stage; a precision mismatch fails the link silently.
  uniform highp float uGrid;
  uniform vec3 uColor;
  uniform vec3 uLightDir;
  uniform float uDebug;

  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vView;
  varying vec3 vLocalN;
  varying vec3 vMean;
  varying vec2 vCell;
  varying float vFlash;
  varying float vSafe;

  void main() {
    if (uDebug > 0.5) {
      // The acceptance probe: the cell encoded in the colour, front only.
      if (vLocalN.z < 0.5) discard;
      gl_FragColor = vec4((vCell.x + 0.5) / uGrid, (vCell.y + 0.5) / uGrid, 1.0, 1.0);
      return;
    }
    vec3 n = normalize(vNormal);
    vec3 v = normalize(vView);
    vec3 l = normalize(uLightDir);
    float lam = max(dot(n, l), 0.0);
    vec3 hv = normalize(l + v);
    float spec = pow(max(dot(n, hv), 0.0), 140.0) * 1.3;
    vec3 col;
    if (vLocalN.z > 0.5) {
      // The fragment: cell (i, j) of the cover, j counted from the top.
      vec2 uv = vec2((vCell.x + vUv.x) / uGrid, 1.0 - (vCell.y + 1.0 - vUv.y) / uGrid);
      vec3 tex = uHasCover > 0.5 ? texture2D(uCover, uv).rgb : vMean;
      col = tex * (0.38 + 0.34 * lam) + vec3(spec);
    } else if (vLocalN.z < -0.5) {
      col = uColor * (0.10 + 0.16 * lam) + vec3(spec * 0.6);
    } else {
      col = vec3(0.55) * (0.35 + 0.5 * lam) + vec3(spec * 0.5);
    }
    col = mix(col, vec3(1.0), vFlash * 0.7);
    col *= vSafe;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// The trail pass: the same tile, flat, in its mean colour. What the
// feedback buffer keeps.
const trailFragmentShader = `
  precision mediump float;

  varying vec3 vMean;
  varying float vSafe;
  varying float vFlash;

  void main() {
    vec3 col = vMean * ${TRAIL_GAIN.toFixed(2)} + vec3(vFlash * 0.3);
    gl_FragColor = vec4(col * vSafe, 1.0);
  }
`;

const feedbackVertexShader = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const feedbackFragmentShader = `
  precision mediump float;

  uniform sampler2D uPrev;
  uniform float uDecay;
  uniform vec2 uTexel;

  varying vec2 vUv;

  void main() {
    vec3 c = texture2D(uPrev, vUv).rgb * 0.5
           + (texture2D(uPrev, vUv + vec2(uTexel.x, 0.0)).rgb
            + texture2D(uPrev, vUv - vec2(uTexel.x, 0.0)).rgb
            + texture2D(uPrev, vUv + vec2(0.0, uTexel.y)).rgb
            + texture2D(uPrev, vUv - vec2(0.0, uTexel.y)).rgb) * 0.125;
    c = c * uDecay - vec3(${FLOOR_SUB.toFixed(5)});
    c = clamp(c, 0.0, ${CEILING.toFixed(2)});
    gl_FragColor = vec4(c, 1.0);
  }
`;

const displayVertexShader = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }
`;

const displayFragmentShader = `
  precision mediump float;
  uniform sampler2D uBuffer;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0); }
`;

// Compiled once and never released (mirrorarray.js's MATERIALS): the
// uniform objects are cached with the materials, so the visualizer adopts
// them and rewrites every value in init.
let MATERIALS = null;

function sharedMaterials() {
  if (MATERIALS) return MATERIALS;
  const tileUniforms = Object.assign({}, safeZoneUniforms, {
    uSafeFloor: { value: SAFE_FLOOR_SWARM },
    uGrid: { value: 20 },
    uMode: { value: 0 },
    uElapsed: { value: 0 },
    uBulge: { value: ASSEMBLE_BULGE },
    uExtra: { value: 0 },
    uPhase: { value: 0 },
    uRadius: { value: SWARM_RADIUS },
    uNCurves: { value: 2 },
    uCurve0: { value: new THREE.Vector4(0, 1, 0, 0) },
    uCurve1: { value: new THREE.Vector4(1, 0.72, 0, 0) },
    uCurve2: { value: new THREE.Vector4(3, 0.5, 0, 0) },
    uParams0: { value: new THREE.Vector4() },
    uParams1: { value: new THREE.Vector4() },
    uParams2: { value: new THREE.Vector4() },
    uTileSwarm: { value: SWARM_TILE_FRAC * VIS_H },
    uTileForm: { value: FORM_SIZE / 20 },
    uPulse: { value: 0 },
    uBeatPhase: { value: 0 },
    uBeatCount: { value: 0 },
    uFlashFrac: { value: SWARM_FLASH_FRAC },
    uForm: { value: FORM_FLAG },
    uFormSize: { value: FORM_SIZE },
    uCopies: { value: 1 },
    uBars: { value: 0 },
    uWaveAmp: { value: 1 },
    uBands: { value: new THREE.Vector3(0.5, 0.5, 0.5) },
    uFormRot: { value: new THREE.Matrix3() },
    uCover: { value: null },
    uHasCover: { value: 0 },
    uColor: { value: new THREE.Color(0x40c0ff) },
    uLightDir: { value: new THREE.Vector3(0.3, 0.6, 1).normalize() },
    uDebug: { value: 0 }
  });
  const feedbackUniforms = {
    uPrev: { value: null },
    uDecay: { value: DECAY_SWARM },
    uTexel: { value: new THREE.Vector2(1 / 1280, 1 / 720) }
  };
  const displayUniforms = { uBuffer: { value: null } };
  MATERIALS = {
    tileUniforms, feedbackUniforms, displayUniforms,
    tile: new THREE.ShaderMaterial({
      uniforms: tileUniforms, vertexShader: tileVertexShader, fragmentShader: tileFragmentShader,
      transparent: false, depthTest: true, depthWrite: true
    }),
    trail: new THREE.ShaderMaterial({
      uniforms: tileUniforms, vertexShader: tileVertexShader, fragmentShader: trailFragmentShader,
      transparent: false, depthTest: true, depthWrite: true
    }),
    feedback: new THREE.ShaderMaterial({
      uniforms: feedbackUniforms, vertexShader: feedbackVertexShader, fragmentShader: feedbackFragmentShader,
      depthTest: false, depthWrite: false
    }),
    display: new THREE.ShaderMaterial({
      uniforms: displayUniforms, vertexShader: displayVertexShader, fragmentShader: displayFragmentShader,
      depthTest: false, depthWrite: false
    })
  };
  return MATERIALS;
}

const MODE_SWARM = 0, MODE_ASSEMBLING = 1, MODE_FORM = 2, MODE_EXPLODING = 3;

export class TexerVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER_LAYOUT[this._tier];
    // ?texerstate=swarm|form freezes a state.
    const st = urlParam('texerstate');
    this._forcedMode = st === 'swarm' ? MODE_SWARM : (st === 'form' ? MODE_FORM : null);
    // ?texerform=flag|cylinder|sphere|cube|wave
    const ff = urlParam('texerform');
    this._forcedForm = ff != null && FORM_NAMES[ff] !== undefined ? FORM_NAMES[ff] : null;
    // ?texerassemble=<f> freezes the assembly at that progress (0..1).
    const fa = parseFloat(urlParam('texerassemble'));
    this._forcedAssemble = Number.isFinite(fa) ? THREE.MathUtils.clamp(fa, 0, 1) : null;
    // ?texercurve=<name> pins the first nested curve's family.
    const fc = urlParam('texercurve');
    this._forcedCurve = fc != null && CURVE_NAMES[fc] !== undefined ? CURVE_NAMES[fc] : null;
    // ?texerdrop=1 fires a drop at start and every 8 bars.
    this._debugDrop = urlParam('texerdrop') === '1';
    // ?texerprobe=1 publishes the live state on window.__texer.
    this._probe = urlParam('texerprobe') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.fov = CAMERA_FOV;
    camera.position.set(0, 0, CAMERA_Z);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();

    const mats = sharedMaterials();
    this._mats = mats;
    this._tu = mats.tileUniforms;
    this._fu = mats.feedbackUniforms;
    this._du = mats.displayUniforms;

    // --- the tiles: one instanced thin box, the cell per instance ---
    const G = this._layout.grid;
    const copies = this._layout.copies;
    const count = G * G * copies;
    this._grid = G;
    this._count = count;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setAttribute('position', box.attributes.position);
    geometry.setAttribute('normal', box.attributes.normal);
    geometry.setAttribute('uv', box.attributes.uv);
    geometry.setIndex(box.index);
    const cell = new Float32Array(count * 2), copy = new Float32Array(count), sArr = new Float32Array(count);
    const axis = new Float32Array(count * 3), rand = new Float32Array(count);
    this._meanArr = new Float32Array(count * 3);
    for (let k = 0; k < count; k++) {
      const cp = Math.floor(k / (G * G)), slot = k % (G * G);
      cell[k * 2] = slot % G; cell[k * 2 + 1] = Math.floor(slot / G);
      copy[k] = cp;
      sArr[k] = (k * 0.6180339887498949) % 1;   // low-discrepancy, static
      _axis.set(Math.random() * 2 - 1, Math.random() * 2 - 1, Math.random() * 2 - 1);
      if (_axis.lengthSq() < 0.05) _axis.set(0, 1, 0);
      _axis.normalize();
      axis[k * 3] = _axis.x; axis[k * 3 + 1] = _axis.y; axis[k * 3 + 2] = _axis.z;
      rand[k] = Math.random();
    }
    geometry.setAttribute('aCell', new THREE.InstancedBufferAttribute(cell, 2));
    geometry.setAttribute('aCopy', new THREE.InstancedBufferAttribute(copy, 1));
    geometry.setAttribute('aS', new THREE.InstancedBufferAttribute(sArr, 1));
    geometry.setAttribute('aAxis', new THREE.InstancedBufferAttribute(axis, 3));
    geometry.setAttribute('aRand', new THREE.InstancedBufferAttribute(rand, 1));
    this._meanAttr = new THREE.InstancedBufferAttribute(this._meanArr, 3);
    geometry.setAttribute('aMean', this._meanAttr);
    geometry.instanceCount = count;
    this._geometry = geometry;
    this._box = box;

    this._tiles = new THREE.Mesh(geometry, mats.tile);
    this._tiles.frustumCulled = false;
    scene.add(this._tiles);

    this._trails = this._layout.trails;
    if (this._trails) {
      this._inner = new THREE.Scene();
      this._trailMesh = new THREE.Mesh(geometry, mats.trail);
      this._trailMesh.frustumCulled = false;
      this._inner.add(this._trailMesh);
      this._buffer = new FeedbackBuffer({ scale: this._layout.scale, depth: true });
      this._feedbackScene = new THREE.Scene();
      this._feedbackQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mats.feedback);
      this._feedbackQuad.frustumCulled = false;
      this._feedbackScene.add(this._feedbackQuad);
      this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      this._display = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mats.display);
      this._display.frustumCulled = false;
      this._display.renderOrder = -1000;
      const self = this;
      this._display.onBeforeRender = function (renderer, outerScene, cam) { self._runPasses(renderer, cam); };
      scene.add(this._display);
    }

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._beatSeconds = 60 / FREE_BPM;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrase = null;
    this._lastMode = null;
    this._beatCount = 0;
    this._barCount = 0;
    this._freeBeatAt = 0;
    this._sinceBeat = 10;
    this._beatPhase = 0;
    this._barPhase = 0;
    this._beats = 0;
    this._bars = 0;
    this._mode = MODE_SWARM;
    this._elapsed = 0;
    this._cycleWasForm = false;
    this._formness = 0;
    this._waveAmp = 1;
    this._form = FORMS[0];
    this._prevFormId = -1;
    this._curves = [];
    this._extra = 0;
    this._extraTarget = 0;
    this._dropT = -1;
    this._lastDropAt = -99;
    this._debugDropAt = 0;
    this._skipFormCycle = -1;
    this._cubeAxis = new THREE.Vector3(0.4, 1, 0.3).normalize();
    this._colorIndex = 0;
    this._hues = [0.55];
    this._cover = null;
    this._coverUrl = null;
    this._coverPending = null;
    this._lastPalette = undefined;
    this._fallbackMeans();

    this._pickCurves();
    this._pickForm();
    this._resetUniforms();
    this._writeUniforms(0.5, 0.5, 0.5);
  }

  _resetUniforms() {
    const tu = this._tu;
    tu.uGrid.value = this._grid;
    tu.uRadius.value = SWARM_RADIUS;
    tu.uTileSwarm.value = SWARM_TILE_FRAC * VIS_H;
    tu.uFormSize.value = FORM_SIZE;
    tu.uPulse.value = 0;
    tu.uWaveAmp.value = 1;
    tu.uDebug.value = 0;
    tu.uCover.value = this._cover;
    tu.uHasCover.value = this._cover ? 1 : 0;
    tu.uExtra.value = 0;
    this._fu.uDecay.value = DECAY_SWARM;
  }

  // --- the choreography --------------------------------------------------------------

  // Two or three nested curves, picked while the tiles are in the form
  // (the swarm is not visible then, so the change is free).
  _pickCurves() {
    const n = 2 + (Math.random() < 0.5 ? 1 : 0);
    const used = new Set();
    this._curves = [];
    for (let i = 0; i < n; i++) {
      let pool = PRESETS.filter((p) => !used.has(p.curve));
      if (i === 0 && this._forcedCurve != null) pool = PRESETS.filter((p) => p.curve === this._forcedCurve);
      const pick = pool[Math.floor(Math.random() * pool.length)];
      used.add(pick.curve);
      this._curves.push(pick);
    }
  }

  // The next form, picked at the start of a cycle (right after the
  // explosion), never the same twice; the cube only where the copies exist.
  _pickForm() {
    let pool = FORMS.filter((f) => f.copies <= this._layout.copies || f.id === FORM_CYLINDER);
    if (this._forcedForm != null) pool = FORMS.filter((f) => f.id === this._forcedForm);
    else if (pool.length > 1) pool = pool.filter((f) => f.id !== this._prevFormId);
    this._form = pool[Math.floor(Math.random() * pool.length)];
    this._prevFormId = this._form.id;
    this._extraTarget = Math.min(this._form.copies, this._layout.copies) > 1 ? 1 : 0;
    this._cubeAxis.set(Math.random() * 2 - 1, 1, Math.random() * 2 - 1).normalize();
    this._colorIndex++;
    this._setColor();
  }

  // --- palette and cover -----------------------------------------------------------

  _applyPalette(profile) {
    const palette = profile && profile.artworkPalette;
    const vivid = (palette && palette.vividColors && palette.vividColors.length) ? palette.vividColors : [0x40c0ff, 0xff4fa0, 0x80ff40];
    this._hues = [];
    for (let i = 0; i < vivid.length; i++) {
      _col.set(vivid[i]).getHSL(_hsl);
      this._hues.push(_hsl.h);
    }
    this._setColor();
    if (!this._cover) this._fallbackMeans();
  }

  _setColor() {
    const h = this._hues[this._colorIndex % this._hues.length];
    this._tu.uColor.value.setHSL(h, 0.85, 0.5);
  }

  // Without a cover (or a tainted image): the palette's hues as means.
  _fallbackMeans() {
    const G = this._grid, arr = this._meanArr;
    for (let k = 0; k < this._count; k++) {
      const slot = k % (G * G);
      const i = slot % G, j = Math.floor(slot / G);
      const h = this._hues[(i * 3 + j * 5) % this._hues.length];
      _col.setHSL(h, 0.8, 0.5);
      arr[k * 3] = _col.r; arr[k * 3 + 1] = _col.g; arr[k * 3 + 2] = _col.b;
    }
    if (this._meanAttr) this._meanAttr.needsUpdate = true;
  }

  // The mean colour of every tile: the cover drawn at G x G, one pixel a
  // tile (the browser's downscale is the mean), read back, linearised.
  _computeMeans(image) {
    const G = this._grid;
    try {
      const cv = document.createElement('canvas');
      cv.width = G; cv.height = G;
      const ctx = cv.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(image, 0, 0, G, G);
      const data = ctx.getImageData(0, 0, G, G).data;
      const arr = this._meanArr;
      for (let k = 0; k < this._count; k++) {
        const slot = k % (G * G);
        const i = slot % G, j = Math.floor(slot / G);
        const o = (j * G + i) * 4;
        _col.setRGB(data[o] / 255, data[o + 1] / 255, data[o + 2] / 255, THREE.SRGBColorSpace);
        arr[k * 3] = _col.r; arr[k * 3 + 1] = _col.g; arr[k * 3 + 2] = _col.b;
      }
      this._meanAttr.needsUpdate = true;
      return true;
    } catch (e) {
      return false;   // tainted canvas: the palette's hues stand in
    }
  }

  _loadCover(url) {
    this._coverUrl = url;
    if (!url) { this._dropCover(); return; }
    const token = {};
    this._coverPending = token;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (texture) => {
      if (this._coverPending !== token) { texture.dispose(); return; }
      this._coverPending = null;
      if (this._cover) this._cover.dispose();
      this._cover = texture;
      this._tu.uCover.value = texture;
      this._tu.uHasCover.value = 1;
      if (!(texture.image && this._computeMeans(texture.image))) this._fallbackMeans();
    }, () => {
      if (this._coverPending === token) { this._coverPending = null; this._dropCover(); }
    });
  }

  _dropCover() {
    if (this._cover) this._cover.dispose();
    this._cover = null;
    this._tu.uCover.value = null;
    this._tu.uHasCover.value = 0;
    this._fallbackMeans();
  }

  onTrackChange(profile) {
    this._applyPalette(profile);
  }

  // --- the frame ---------------------------------------------------------------------

  update(deltaTime, profile) {
    const director = profile.director || {};
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    if (profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._applyPalette(profile); }
    if ((profile.artworkUrl || null) !== this._coverUrl) this._loadCover(profile.artworkUrl || null);
    if (paused) return;   // frozen: no clock, no feedback pass

    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 220) : FREE_BPM;
    this._beatSeconds = 60 / bpm;
    const beat = this._beatSeconds;
    const measured = !!director.hasReliableGrid;
    const bands = (measured && director.bands) || null;
    const low = bands ? (bands.low || 0) : 0.5;
    const mid = bands ? (bands.mid || 0) : 0.5;
    const high = bands ? (bands.high || 0) : 0.5;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    this._sinceBeat += dt;
    const t = this._time;

    // --- the clocks ---
    let beatEdge = false, barEdge = false, phraseEdge = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
      this._lastBeatPhase = profile.beatPhase;
    } else if (t - this._freeBeatAt >= beat) { this._freeBeatAt = t; beatEdge = true; }
    if (beatEdge) { this._beatCount++; this._sinceBeat = 0; }
    this._beatPhase = profile.beatPhase != null ? profile.beatPhase : Math.min(1, (t - this._freeBeatAt) / beat);
    this._beats = this._beatCount + this._beatPhase;
    if (profile.barPhase != null) {
      if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase) barEdge = true;
      this._lastBarPhase = profile.barPhase;
      this._barPhase = profile.barPhase;
    } else {
      if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
      this._barPhase = ((this._beatCount % 4) + this._beatPhase) / 4;
    }
    if (barEdge) this._barCount++;
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
    this._lastPhrase = phrase;
    // The director's phrases are four bars: a phrase edge re-aligns the
    // eight-bar cycle on them.
    if (phraseEdge && this._barCount % 4 !== 0) this._barCount = Math.round(this._barCount / 4) * 4;
    this._bars = this._barCount + this._barPhase;

    // --- the drop: the flag at once, four bars, then the explosion ---
    let dropNow = false;
    if (measured && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
    this._lastMode = director.mode;
    if (this._debugDrop && t >= this._debugDropAt) { dropNow = true; this._debugDropAt = t + 8 * 4 * beat; }
    if (dropNow && this._beats - this._lastDropAt > 8 && this._forcedMode == null) {
      this._lastDropAt = this._beats;
      this._dropT = 0;
      this._form = FORMS[FORM_FLAG];
      this._prevFormId = FORM_FLAG;
      this._extraTarget = 0;
    }
    if (this._dropT >= 0) this._dropT += dt / beat;

    // --- the mode, from the cycle or the drop ---
    const cycleBeat = (this._barCount % CYCLE_BARS) * 4 + this._barPhase * 4;
    const cycleIndex = Math.floor(this._barCount / CYCLE_BARS);
    const assembleTotal = ASSEMBLE_BEATS + ASSEMBLE_DELAY;
    const explodeTotal = EXPLODE_BEATS + EXPLODE_DELAY;
    let mode, elapsed, waveAmp = 1;
    if (this._dropT >= 0) {
      const d = this._dropT;
      const hold = assembleTotal + DROP_HOLD_BARS * 4;
      if (d < assembleTotal) { mode = MODE_ASSEMBLING; elapsed = d; }
      else if (d < hold) { mode = MODE_FORM; elapsed = d - assembleTotal; }
      else if (d < hold + explodeTotal) { mode = MODE_EXPLODING; elapsed = d - hold; }
      else { this._dropT = -1; this._skipFormCycle = cycleIndex; mode = MODE_SWARM; elapsed = 0; this._pickCurves(); this._pickForm(); }
      waveAmp = DROP_WAVE;
    } else {
      const assembleAt = FORM_AT_BEAT - assembleTotal;
      if (cycleBeat < explodeTotal) { mode = this._cycleWasForm ? MODE_EXPLODING : MODE_SWARM; elapsed = cycleBeat; }
      else if (cycleBeat < assembleAt || this._skipFormCycle === cycleIndex) { mode = MODE_SWARM; elapsed = 0; }
      else if (cycleBeat < FORM_AT_BEAT) { mode = MODE_ASSEMBLING; elapsed = cycleBeat - assembleAt; }
      else { mode = MODE_FORM; elapsed = cycleBeat - FORM_AT_BEAT; }
    }
    // The transitions of the cycle: a new form once the explosion is over,
    // new curves once the form is complete (the swarm is not visible then).
    if (mode === MODE_SWARM && this._mode === MODE_EXPLODING) { this._pickForm(); this._pickCurves(); }
    if (mode === MODE_FORM && this._mode === MODE_ASSEMBLING) this._pickCurves();
    if (mode === MODE_FORM) this._cycleWasForm = true;
    if (mode === MODE_SWARM) this._cycleWasForm = false;
    if (this._forcedMode != null) { mode = this._forcedMode; elapsed = 0; }
    if (this._forcedAssemble != null) { mode = MODE_ASSEMBLING; elapsed = this._forcedAssemble * assembleTotal; }
    this._mode = mode;
    this._elapsed = elapsed;
    // How "in the form" the frame is, for the decay and the safe floor.
    const f = mode === MODE_FORM ? 1 : (mode === MODE_SWARM ? 0 : (mode === MODE_ASSEMBLING ? elapsed / assembleTotal : 1 - elapsed / explodeTotal));
    this._formness = THREE.MathUtils.clamp(f, 0, 1);
    // The extra copies fade in and out in the swarm.
    this._extra += (this._extraTarget - this._extra) * Math.min(1, dt / (EXTRA_FADE_BEATS * beat) * 3);
    this._waveAmp = waveAmp;

    this._writeUniforms(low, mid, high);
    if (this._probe && typeof window !== 'undefined') window.__texer = this.getProbe();
  }

  _writeUniforms(low, mid, high) {
    const tu = this._tu;
    tu.uMode.value = this._mode;
    tu.uElapsed.value = this._elapsed;
    tu.uBulge.value = this._mode === MODE_EXPLODING ? EXPLODE_BULGE : ASSEMBLE_BULGE;
    tu.uExtra.value = this._extra;
    tu.uPhase.value = this._barPhase;
    tu.uBars.value = this._bars;
    tu.uNCurves.value = this._curves.length;
    const cs = [tu.uCurve0, tu.uCurve1, tu.uCurve2], ps = [tu.uParams0, tu.uParams1, tu.uParams2];
    for (let i = 0; i < 3; i++) {
      const c = this._curves[Math.min(i, this._curves.length - 1)];
      cs[i].value.set(c.curve, NESTED_SCALES[i], 0, 0);
      ps[i].value.set(c.p[0], c.p[1], c.p[2], c.p[3]);
    }
    tu.uRadius.value = SWARM_RADIUS * (1 + 0.15 * (low * 2 - 1));
    tu.uTileForm.value = (FORM_SIZE / 20) * this._form.tile;
    tu.uForm.value = this._form.id;
    tu.uCopies.value = Math.min(this._form.copies, this._layout.copies);
    tu.uWaveAmp.value = this._waveAmp || 1;
    tu.uBands.value.set(low, mid, high);
    _m4.makeRotationAxis(this._cubeAxis, Math.PI * 2 * this._bars / CUBE_SPIN_BARS);
    tu.uFormRot.value.setFromMatrix4(_m4);
    const sb = this._sinceBeat;
    tu.uPulse.value = sb < PULSE_ATTACK_S ? sb / PULSE_ATTACK_S : Math.max(0, 1 - (sb - PULSE_ATTACK_S) / PULSE_RELEASE_S);
    tu.uBeatPhase.value = this._beatPhase;
    tu.uBeatCount.value = this._beatCount % 97;
    tu.uFlashFrac.value = SWARM_FLASH_FRAC * (0.6 + 0.8 * high);
    const la = Math.PI * 2 * this._bars / LIGHT_BARS;
    tu.uLightDir.value.set(Math.cos(la), 0.6, Math.sin(la) * 0.5 + 0.7).normalize();
    tu.uSafeFloor.value = SAFE_FLOOR_SWARM + (SAFE_FLOOR_FORM - SAFE_FLOOR_SWARM) * this._formness;
    this._fu.uDecay.value = DECAY_SWARM + (DECAY_FORM - DECAY_SWARM) * this._formness;
  }

  // The render-target work, from the display quad's onBeforeRender: decay
  // the previous frame, inject the tiles in their mean colours with the
  // real camera, swap, display. The lit tiles are drawn by the shared scene
  // afterwards, on top.
  _runPasses(renderer, cam) {
    const rebuilt = this._buffer.ensureSize(renderer);
    if (rebuilt) {
      this._du.uBuffer.value = this._buffer.read.texture;
      this._fu.uTexel.value.set(1 / this._buffer.width, 1 / this._buffer.height);
    }
    if (this._paused) { this._du.uBuffer.value = this._buffer.read.texture; return; }
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    this._fu.uPrev.value = this._buffer.read.texture;
    renderer.setRenderTarget(this._buffer.write);
    renderer.autoClear = false;
    renderer.render(this._feedbackScene, this._ortho);
    renderer.clearDepth();
    renderer.render(this._inner, cam);
    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
    this._buffer.swap();
    this._du.uBuffer.value = this._buffer.read.texture;
  }

  getProbe() {
    return {
      t: this._time,
      beats: this._beats,
      bars: this._bars,
      barPhase: this._barPhase,
      mode: this._mode,
      elapsed: this._elapsed,
      formness: this._formness,
      form: this._form.id,
      copies: Math.min(this._form.copies, this._layout.copies),
      extra: this._extra,
      curves: this._curves.map((c) => ({ curve: c.curve, p: c.p })),
      drop: this._dropT,
      cover: !!this._cover,
      decay: this._fu.uDecay.value,
      grid: this._grid,
      count: this._count,
      formSize: FORM_SIZE,
      tileForm: this._tu.uTileForm.value
    };
  }

  dispose() {
    this._coverPending = null;
    this._scene.remove(this._tiles);
    if (this._trails) {
      this._inner.remove(this._trailMesh);
      this._display.onBeforeRender = null;
      this._scene.remove(this._display);
      this._display.geometry.dispose();
      this._feedbackQuad.geometry.dispose();
      this._buffer.dispose();
      this._du.uBuffer.value = null;
      this._fu.uPrev.value = null;
    }
    this._geometry.dispose();           // the MATERIALS are cached - see sharedMaterials
    this._box.dispose();
    if (this._cover) { this._cover.dispose(); this._cover = null; }
    this._tu.uCover.value = null;
    this._tu.uHasCover.value = 0;
    if (typeof window !== 'undefined' && window.__texer) delete window.__texer;
    this._scene = null;
  }
}
